// Helpers shared by the site-specific agency `parseDetailHtml` parsers
// (jellis-craig, belle-property, barry-plant, toop-and-toop, realmark,
// allhomes-com-au, buxton). Providers only fetch raw HTML; these parsers read
// each site's own markup or embedded data, so every helper here is strict:
// it returns null rather than a guess.

const ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  apos: "'",
  quot: '"',
  lt: "<",
  gt: ">",
  nbsp: " ",
  ndash: "–",
  mdash: "—",
  rsquo: "’",
  lsquo: "‘",
  sup2: "²",
};

export function decodeEntities(text: string): string {
  return text.replace(/&(#x[\da-f]+|#\d+|[a-z\d]+);/gi, (whole, code: string) => {
    if (code[0] === "#") {
      const point =
        code[1] === "x" || code[1] === "X"
          ? Number.parseInt(code.slice(2), 16)
          : Number.parseInt(code.slice(1), 10);
      return Number.isFinite(point) ? String.fromCodePoint(point) : whole;
    }
    return ENTITIES[code.toLowerCase()] ?? whole;
  });
}

/** Visible text of an HTML fragment: tags dropped, entities decoded, whitespace collapsed. */
export function htmlText(fragment: string): string {
  return decodeEntities(fragment.replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim();
}

export function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** `<meta name|property="key" content="…">` in either attribute order, decoded. */
export function readMeta(html: string, key: string): string | null {
  const name = escapeRegExp(key);
  const match =
    html.match(
      new RegExp(`<meta[^>]*(?:name|property)=["']${name}["'][^>]*content=["']([^"']*)["']`, "i"),
    ) ??
    html.match(
      new RegExp(`<meta[^>]*content=["']([^"']*)["'][^>]*(?:name|property)=["']${name}["']`, "i"),
    );
  return match ? text(decodeEntities(match[1])) : null;
}

export function readTitle(html: string): string | null {
  const match = html.match(/<title[^>]*>([^<]*)<\/title>/i);
  return match ? text(htmlText(match[1])) : null;
}

/**
 * Parse the JSON object that starts at `source[start]` (which must be "{"),
 * reading to its matching close brace. For objects embedded in scripts
 * (`window.X = {…};`, a JSON value inside a larger payload). Null when the
 * text there is not one complete, valid JSON object.
 */
export function readJsonObjectAt(source: string, start: number): Record<string, unknown> | null {
  if (source[start] !== "{") return null;
  let depth = 0;
  let inString = false;
  for (let i = start; i < source.length; i++) {
    const ch = source[i];
    if (inString) {
      if (ch === "\\") i++;
      else if (ch === '"') inString = false;
    } else if (ch === '"') {
      inString = true;
    } else if (ch === "{") {
      depth++;
    } else if (ch === "}" && --depth === 0) {
      try {
        const parsed: unknown = JSON.parse(source.slice(start, i + 1));
        return isRecord(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

function isoDate(year: number, month: number, day: number): string | null {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * A listing-page date → `YYYY-MM-DD`. Accepts ISO "2025-09-13[T…]", read as
 * written and never shifted through a timezone, and "Sep 13, 2025" (Jellis
 * Craig's sold line). Anything else, including an impossible date → null.
 */
export function parseListingDate(value: string | null | undefined): string | null {
  const raw = text(value);
  if (!raw) return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return isoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const monthFirst = raw.match(/^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/);
  const month = monthFirst ? MONTHS.indexOf(monthFirst[1].slice(0, 3).toLowerCase()) + 1 : 0;
  return monthFirst && month > 0
    ? isoDate(Number(monthFirst[3]), month, Number(monthFirst[2]))
    : null;
}

/**
 * The sold amount in a price line, or null. The line must be one dollar
 * figure and nothing else, after an optional "Sold" / "Sold for" / "Sold
 * Price:" lead: "$1,175,000", "Sold $635,000", "Sold for $1.2m" → a number.
 * A range ("$1.3m - $1.4m"), a guide or offer ("Offers over $900,000"), a
 * rent ("$650 per week"), withheld text or a figure under $10,000 → null.
 */
export function soldAmount(display: string | null | undefined): number | null {
  const raw = text(display);
  if (!raw) return null;
  const line = raw.replace(/^(?:sold|sale)(?:\s+(?:price|for|at))*\s*:?\s*/i, "");
  const match = line.match(/^\$\s*([\d,]+(?:\.\d+)?)\s*([mk])?\.?$/i);
  if (!match) return null;
  const unit = match[2]?.toLowerCase();
  const factor = unit === "m" ? 1_000_000 : unit === "k" ? 1_000 : 1;
  const value = Math.round(Number(match[1].replace(/,/g, "")) * factor);
  return Number.isFinite(value) && value >= 10_000 && value < 1_000_000_000 ? value : null;
}

/** A bed/bath/car count: a whole number 0..50 (numeric or numeric string), else null. */
export function count(value: unknown): number | null {
  const n = typeof value === "string" && /^\s*\d+\s*$/.test(value) ? Number(value) : value;
  return typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 50 ? n : null;
}

const STATE_NAMES: Readonly<Record<string, string>> = {
  victoria: "VIC",
  "new south wales": "NSW",
  queensland: "QLD",
  "south australia": "SA",
  "western australia": "WA",
  tasmania: "TAS",
  "australian capital territory": "ACT",
  "northern territory": "NT",
};

export function stateCode(value: string | null | undefined): string | null {
  const raw = text(value);
  if (!raw) return null;
  if (/^(VIC|NSW|QLD|SA|WA|TAS|ACT|NT)$/i.test(raw)) return raw.toUpperCase();
  return STATE_NAMES[raw.toLowerCase()] ?? null;
}

/** "1/1 Carmichael Street, West Footscray VIC 3012" — the format Domain's parser writes. */
export function formatAddress(
  street: string | null,
  suburb: string | null,
  state: string | null,
  postcode: string | null,
): string | null {
  if (!street) return null;
  const locality = [suburb, state, postcode].filter(Boolean).join(" ");
  return locality ? `${street}, ${locality}` : street;
}

/** "WEST FOOTSCRAY" / "west footscray" → "West Footscray"; mixed case is kept as written. */
export function suburbName(value: string | null | undefined): string | null {
  const raw = text(value);
  if (!raw) return null;
  if (raw !== raw.toUpperCase() && raw !== raw.toLowerCase()) return raw;
  return raw
    .toLowerCase()
    .replace(/(^|[\s-])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

// Property types a listing can genuinely have. Anything else a site emits as
// its "type" (a schema.org class such as "WebSite", an architectural style
// such as "Edwardian", a sale method) is not a property type and is dropped.
const PROPERTY_TYPES: Readonly<Record<string, string>> = {
  house: "House",
  home: "House",
  townhouse: "Townhouse",
  "town house": "Townhouse",
  terrace: "Terrace",
  unit: "Unit",
  apartment: "Apartment",
  "apartment / unit / flat": "Apartment",
  "apartment/unit/flat": "Apartment",
  "apartment unit flat": "Apartment",
  flat: "Flat",
  studio: "Studio",
  villa: "Villa",
  duplex: "Duplex",
  "semi-detached": "Semi-Detached",
  "semi detached": "Semi-Detached",
  penthouse: "Penthouse",
  land: "Land",
  "vacant land": "Land",
  "residential land": "Land",
  acreage: "Acreage",
  "acreage / semi-rural": "Acreage",
  rural: "Rural",
  "block of units": "Block of Units",
  retirement: "Retirement",
  "retirement living": "Retirement",
};

export function propertyTypeLabel(value: string | null | undefined): string | null {
  const raw = text(value);
  return raw ? (PROPERTY_TYPES[raw.toLowerCase().replace(/[_\s]+/g, " ")] ?? null) : null;
}

/** "843", "1,012.5" → "843", "1012.5" (the landSize string shape); zero or junk → null. */
export function areaValue(value: unknown): string | null {
  const raw = typeof value === "number" ? String(value) : text(value)?.replace(/,/g, "");
  if (!raw || !/^\d+(?:\.\d+)?$/.test(raw)) return null;
  return Number(raw) > 0 ? String(Number(raw)) : null;
}
